import { Link } from "react-router-dom";
import { ITAAS_ORGANIZATION_ID } from "@itaas/pep-contracts";
import { PepLogo } from "../components/brand/PepLogo";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { usePepI18n } from "../i18n/usePepI18n";
import { SITE_URL } from "../config";
import { DiffIcon, DomainIcon } from "../home/HomeIcons";
import { PremiumSolutionsCarousel } from "../home/PremiumSolutionsCarousel";
import {
  getPublicProductCaptures,
  HOME_DIFF_POINTS,
  HOME_DOMAINS,
  HOME_PUBLISHER,
  homeCopy,
} from "../home/home-content";

/**
 * PEP-2.5 Home — approved IA + visual rhythm (pass / refine / redesign).
 * No vanity stats, no fabricated product UI, no public HS placeholders.
 */
export function HomePage() {
  const { locale } = usePepI18n();
  const en = locale === "en";
  const copy = homeCopy(locale);
  const publicCaptures = getPublicProductCaptures();
  const showProductProof = publicCaptures.length > 0;

  return (
    <SiteLayout>
      <SeoHead
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={[
          "ITaaS Suite",
          "ITaaS RDC",
          "logiciel métier Lubumbashi",
          "plateforme logicielle RDC",
          "ERP RH éducation mines Congo",
        ]}
        canonicalPath="/"
        locale={locale}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: copy.seoTitle,
          description: copy.seoDescription,
          url: SITE_URL,
          isPartOf: { "@id": `${SITE_URL}/#website` },
          publisher: { "@id": ITAAS_ORGANIZATION_ID },
        }}
      />

      {/* H-IDENTITY — premium Hero (approved HD art direction) */}
      <section
        className="pep-home-hero pep-home-hero--split pep-home-hero--premium"
        aria-labelledby="home-h1"
        data-section="H-IDENTITY"
      >
        <div className="container pep-home-hero__grid">
          <div className="pep-home-hero__copy">
            <p className="pep-home-eyebrow">{copy.eyebrow}</p>
            <h1 id="home-h1" className="pep-home-h1">
              {copy.h1Lead}{" "}
              <span className="pep-home-h1__emphasis">{copy.h1Emphasis}</span>
            </h1>
            <p className="pep-home-lead">{copy.support}</p>
            <div className="pep-home-cta-pair">
              <Link className="btn btn-primary btn-lg pep-home-btn" to={copy.demoHref}>
                {copy.primaryCta} →
              </Link>
              <Link className="btn btn-outline-primary btn-lg pep-home-btn" to={copy.productsHref}>
                {copy.secondaryCta} →
              </Link>
            </div>
            <p className="pep-home-trust-line">
              <span className="pep-home-trust-line__icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3c2.2 1.1 3.8 2.2 4.6 3.8.7 1.4.8 2.9.2 4.2-.5 1.1-1.4 2-2.4 2.9l-.8.7c-.4.4-.7.9-.7 1.4v2.2c0 .5-.2 1-.6 1.3L11 21"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3C9.2 4.4 7.4 6 6.6 7.8c-.7 1.5-.5 3.1.4 4.5.8 1.2 2 2.2 3.2 3.1.5.4.9.9.9 1.5v1.6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {copy.trustChip}
            </p>
          </div>
          <figure className="pep-home-hero__visual">
            <div className="pep-home-hero__glow" aria-hidden="true" />
            <div className="pep-home-hero__art">
              <img
                className="pep-home-concept-img pep-home-concept-img--hero"
                src={copy.heroImageSrc}
                alt={copy.heroVisualAlt}
                width={1536}
                height={1024}
                decoding="async"
                fetchPriority="high"
              />
              <ul className="pep-home-hero__domain-labels list-unstyled mb-0" aria-label={copy.heroDomainsLabel}>
                {copy.heroDomains.map((domain) => (
                  <li
                    key={domain.id}
                    className={`pep-home-hero__domain-label pep-home-hero__domain-label--${domain.id}`}
                  >
                    {domain.label}
                  </li>
                ))}
              </ul>
            </div>
            <figcaption className="visually-hidden">{copy.heroVisualAlt}</figcaption>
          </figure>
        </div>
      </section>

      {/* H-VALUE — commercial conclusion of Hero (not a second hero) */}
      <section
        className="pep-home-value"
        aria-labelledby="home-value-h2"
        data-section="H-VALUE"
      >
        <div className="container">
          <div className="pep-home-value__panel">
            <div className="pep-home-value__intro">
              <p className="pep-home-value__eyebrow">{copy.valueEyebrow}</p>
              <h2 id="home-value-h2" className="pep-home-h2 pep-home-h2--sm mb-2">
                {copy.valueH2}
              </h2>
              <p className="pep-home-value__lead mb-0">{copy.valueIntro}</p>
            </div>
            <ul className="pep-home-value__pillars list-unstyled mb-0">
              {copy.valuePillars.map((pillar) => (
                <li key={pillar.id} className={`pep-home-value__pillar pep-home-value__pillar--${pillar.id}`}>
                  <span className="pep-home-value__icon" aria-hidden="true">
                    {pillar.id === "security" ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 3 19 6.5v5.2c0 4.2-2.9 7.8-7 9.3-4.1-1.5-7-5.1-7-9.3V6.5L12 3Z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                    {pillar.id === "data" ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M4 19V9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M10 19V5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M16 19v-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <path d="M20 19H3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    ) : null}
                    {pillar.id === "scale" ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M14.5 9.5 5 19l.7 2.3L8 21l9.5-9.5"
                          stroke="currentColor"
                          strokeWidth="1.55"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14.5 9.5c1.2-3.4 4.1-5.7 7.5-6.5-.8 3.4-3.1 6.3-6.5 7.5Z"
                          stroke="currentColor"
                          strokeWidth="1.55"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.8 14.2 4 15.5M9.8 18.2 8.5 20"
                          stroke="currentColor"
                          strokeWidth="1.55"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : null}
                  </span>
                  <h3 className="pep-home-value__pillar-title">{pillar.title}</h3>
                  <p className="pep-home-value__pillar-body mb-0">{pillar.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* H-PROOF — first visual break: light blue + ecosystem illustration */}
      <section
        className="pep-home-section pep-home-section--blue"
        aria-labelledby="home-proof-h2"
        data-section="H-PROOF"
      >
        <div className="container pep-home-ecosystem">
          <header className="pep-home-section__head pep-home-ecosystem__intro">
            <h2 id="home-proof-h2" className="pep-home-h2">
              {copy.proofH2}
            </h2>
            <p className="pep-home-section__lead">{copy.proofBody}</p>
            {/* H-PLATFORM narrative merged here — no standalone section */}
            <p className="pep-home-ecosystem__suite mb-0">
              <strong className="pep-home-ecosystem__suite-title">{copy.platformH2}</strong>
              <span className="pep-home-ecosystem__suite-body">{copy.platformBody}</span>
            </p>
          </header>
          <figure className="pep-home-ecosystem-visual">
            <img
              className="pep-home-concept-img pep-home-concept-img--ecosystem"
              src="/assets/home/ecosystem-composition.png"
              alt={copy.ecosystemVisualAlt}
              width={1280}
              height={720}
              loading="lazy"
              decoding="async"
            />
            <figcaption className="pep-home-concept-caption">
              {en
                ? "Conceptual illustration — not a product screenshot."
                : "Illustration conceptuelle — ce n’est pas une capture produit."}
            </figcaption>
          </figure>
        </div>
      </section>

      {/* H-DOMAINS — refined domain cards (distinct from Solutions rows) */}
      <section
        className="pep-home-section pep-home-section--white"
        aria-labelledby="home-domains-h2"
        data-section="H-DOMAINS"
      >
        <div className="container">
          <header className="pep-home-section__head">
            <h2 id="home-domains-h2" className="pep-home-h2">
              {copy.domainsH2}
            </h2>
          </header>
          <ul className="pep-home-domain-grid list-unstyled mb-0">
            {HOME_DOMAINS.map((domain) => (
              <li key={domain.id}>
                <Link className={`pep-home-domain-card pep-home-domain-card--${domain.id}`} to={domain.href}>
                  <span className="pep-home-domain-card__icon">
                    <DomainIcon name={domain.icon} />
                  </span>
                  <span className="pep-home-domain-card__title">
                    {en ? domain.titleEn : domain.titleFr}
                  </span>
                  <span className="pep-home-domain-card__product">
                    {en ? domain.productHintEn : domain.productHintFr}
                  </span>
                  <span className="pep-home-domain-card__text">
                    {en ? domain.outcomeEn : domain.outcomeFr}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <aside className="pep-home-limited pep-home-limited--bar" aria-label={copy.healthNoteLabel}>
            <p className="pep-home-limited__label mb-0">{copy.healthNoteLabel}</p>
            <p className="mb-0">
              <Link className="pep-home-text-link" to={copy.healthNoteHref}>
                {copy.healthNoteProduct}
              </Link>
            </p>
          </aside>
        </div>
      </section>

      {/* H-SOLUTIONS — premium catalog carousel (approved card system preserved) */}
      <section
        className="pep-home-section pep-home-section--mist"
        aria-labelledby="home-solutions-h2"
        data-section="H-SOLUTIONS"
      >
        <div className="container">
          <PremiumSolutionsCarousel
            locale={locale === "en" ? "en" : "fr"}
            title={
              <h2 id="home-solutions-h2" className="pep-home-h2 mb-0">
                {copy.solutionsH2}
              </h2>
            }
            limitedLabel={copy.solutionsLimitedLabel}
            prevLabel={copy.carouselPrev}
            nextLabel={copy.carouselNext}
          />
          <p className="pep-home-solutions__editorial-note visually-hidden">{copy.solutionsEditorialNote}</p>
          <div className="pep-home-solutions__footer">
            <Link className="btn btn-outline-primary pep-home-solutions__catalog" to={copy.productsHref}>
              {copy.solutionsCatalog} →
            </Link>
            <aside className="pep-home-limited pep-home-limited--solutions" aria-label={copy.solutionsLimitedLabel}>
              <p className="pep-home-limited__label mb-1">{copy.solutionsLimitedLabel}</p>
              <p className="mb-0 small">{copy.solutionsLimitedBody}</p>
            </aside>
          </div>
        </div>
      </section>

      {/* H-PRODUCT-PROOF — navy moment; REAL captures only (never public placeholders) */}
      {showProductProof ? (
        <section
          className="pep-home-product-proof"
          aria-labelledby="home-product-proof-h2"
          data-section="H-PRODUCT-PROOF"
        >
          <div className="container">
            <header className="pep-home-section__head pep-home-product-proof__head">
              <h2 id="home-product-proof-h2" className="pep-home-h2 pep-home-h2--on-dark">
                {copy.productProofH2}
              </h2>
              <p className="pep-home-section__lead pep-home-section__lead--on-dark mb-0">
                {copy.productProofBody}
              </p>
            </header>
            <div className="pep-home-proof-grid">
              {publicCaptures.map((slot) => (
                <figure key={slot.id} className="pep-home-capture pep-home-capture--real">
                  <img
                    src={slot.publicSrc}
                    alt={en ? slot.productLabelEn : slot.productLabelFr}
                    loading="lazy"
                  />
                  <figcaption className="pep-home-capture__caption">
                    {en ? slot.productLabelEn : slot.productLabelFr}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* H-DIFF — editorial columns (Why ITaaS) */}
      <section
        className="pep-home-section pep-home-section--page"
        aria-labelledby="home-diff-h2"
        data-section="H-DIFF"
      >
        <div className="container">
          <header className="pep-home-section__head pep-home-section__head--center">
            <h2 id="home-diff-h2" className="pep-home-h2">
              {copy.diffH2}
            </h2>
          </header>
          <ol className="pep-home-diff list-unstyled mb-0">
            {HOME_DIFF_POINTS.map((point, index) => (
              <li key={point.id} className="pep-home-diff__item">
                <span className="pep-home-diff__badge" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="pep-home-diff__icon">
                  <DiffIcon name={point.id as "suite" | "local" | "modular"} />
                </span>
                <p className="pep-home-diff__text mb-0">{en ? point.en : point.fr}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* H-TRUST — institutional two-column company band */}
      <section
        className="pep-home-trust pep-home-trust--blue"
        aria-labelledby="home-trust-h2"
        data-section="H-TRUST"
      >
        <div className="container pep-home-trust__grid">
          <div className="pep-home-trust__brand">
            <div className="pep-home-trust__logo">
              <PepLogo variant="compact" />
            </div>
            <h2 id="home-trust-h2" className="pep-home-h2 pep-home-h2--sm">
              {copy.trustH2}
            </h2>
            <p className="pep-home-trust__role mb-1">
              <strong>{HOME_PUBLISHER.brandName}</strong>
              {" — "}
              {en ? HOME_PUBLISHER.roleEn : HOME_PUBLISHER.roleFr}
            </p>
            <p className="pep-home-trust__locality mb-3">
              {en ? HOME_PUBLISHER.localityLineEn : HOME_PUBLISHER.localityLineFr}
            </p>
            <Link className="pep-home-text-link" to={copy.aboutHref}>
              {copy.aboutLink} →
            </Link>
          </div>
          <address className="pep-home-trust__nap mb-0">
            {HOME_PUBLISHER.addressLines.map((line) => (
              <span key={line} className="d-block">
                {line}
              </span>
            ))}
            <a className="d-block mt-2" href={`mailto:${HOME_PUBLISHER.email}`}>
              {HOME_PUBLISHER.email}
            </a>
            <a className="d-block" href={`tel:${HOME_PUBLISHER.phone.replace(/\s+/g, "")}`}>
              {HOME_PUBLISHER.phone}
            </a>
          </address>
        </div>
      </section>

      {/* H-ACTION — accent conversion band (composition refined) */}
      <section
        className="pep-home-action pep-home-action--accent"
        aria-labelledby="home-action-h2"
        data-section="H-ACTION"
      >
        <div className="container">
          <div className="pep-home-action__inner">
            <div className="pep-home-action__copy">
              <span className="pep-home-action__mark" aria-hidden="true">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect x="3" y="14" width="10" height="10" rx="2" fill="currentColor" opacity="0.9" />
                  <rect x="15" y="4" width="10" height="10" rx="2" fill="currentColor" opacity="0.55" />
                  <rect x="15" y="16" width="10" height="8" rx="2" fill="currentColor" opacity="0.35" />
                </svg>
              </span>
              <div>
                <h2 id="home-action-h2" className="pep-home-h2 pep-home-h2--on-dark mb-2">
                  {copy.actionH2}
                </h2>
                <p className="pep-home-action__support pep-home-action__support--on-dark mb-0">
                  {copy.actionSupport}
                </p>
              </div>
            </div>
            <div className="pep-home-cta-pair pep-home-cta-pair--action">
              <Link className="btn btn-light btn-lg pep-home-btn pep-home-btn--on-accent" to={copy.demoHref}>
                {copy.primaryCta}
              </Link>
              <Link className="btn btn-link pep-home-btn pep-home-btn--ghost" to={copy.contactHref}>
                {copy.actionSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
